import {Context} from 'chartjs-plugin-datalabels';

import {ChartTooltip} from '../../../../../../component/chart-tooltip/chart-tooltip';
import {ChartTooltipDivider} from '../../../../../../component/chart-tooltip/chart-tooltip-divider';
import {getCompareColor} from '../../../../../../component/chart-tooltip/chart-tooltip-helper';
import {ChartTooltipRow} from '../../../../../../component/chart-tooltip/chart-tooltip-row';
import {ChartTooltipTitle} from '../../../../../../component/chart-tooltip/chart-tooltip-title';
import {DefaultTooltipStaticPropsType} from '../../../../../../component/chart-tooltip/default-chart-tooltip';
import {defaultLineChartOptions} from '../../../../../../layout/chart/line-chart/line-chart-helper';
import {ChartJsLineOptionsType} from '../../../../../../layout/chart/line-chart/line-chart-type';
import {radarChartOptions} from '../../../../../../layout/chart/radar-chart/radar-chart-helper';
import {ChartJsRadarOptionsType, RadarDataSetType} from '../../../../../../layout/chart/radar-chart/radar-chart-type';
import {customReactChartTooltip} from '../../../../../../layout/chart/utils/chart-helper';
import {
    ReviewAspectRecordType,
    ReviewsByAspectsDetailedItemType,
} from '../../../../../../service/reivew-analysis/review-aspects/review-aspects-type';
import {
    SentimentDataType,
    SentimentEnum,
} from '../../../../../../service/reivew-analysis/review-sentiment/review-sentiment-type';
import * as reviewAnalysisPaletteStyles from '../../../reviews-analysis-palette-export.scss';

import {aspectMinTotalReviews, aspectStrongThreshold, aspectWeakThreshold} from './aspects-tab-const';
import {AspectStrengthEnum} from './aspects-tab-type';

type TonalityPeriodDataType = ReviewAspectRecordType['tonalityCurrentPeriodData'];

export function getTonalitySentimentData(tonalityData?: TonalityPeriodDataType | null): SentimentDataType {
    return {
        [SentimentEnum.Negative]: tonalityData?.negative ?? 0,
        [SentimentEnum.Neutral]: tonalityData?.neutral ?? 0,
        [SentimentEnum.Positive]: tonalityData?.positive ?? 0,
        [SentimentEnum.Unspecified]: 0,
    };
}

// BST-10541: classify an aspect as a strong/weak side by its tonality index.
// Not classified (returns null) when there are too few mentions to be meaningful.
export function getAspectStrength(tonalityIndex: number, totalReviews: number): AspectStrengthEnum | null {
    if (totalReviews < aspectMinTotalReviews) {
        return null;
    }

    if (tonalityIndex >= aspectStrongThreshold) {
        return AspectStrengthEnum.Strong;
    }

    if (tonalityIndex <= aspectWeakThreshold) {
        return AspectStrengthEnum.Weak;
    }

    return AspectStrengthEnum.Neutral;
}

// BST-10541: color the tonality-index cells on the same [-100..+100] thresholds the
// strong/weak classifier uses, so a cell's color never contradicts its bucket
// (e.g. an index of 55 is a strong side AND green).
export function getTonalityBandColor(tonalityIndex: number): string {
    if (tonalityIndex >= aspectStrongThreshold) {
        return reviewAnalysisPaletteStyles.sentiment_positive;
    }

    if (tonalityIndex <= aspectWeakThreshold) {
        return reviewAnalysisPaletteStyles.sentiment_negative;
    }

    return reviewAnalysisPaletteStyles.sentiment_neutral;
}

// Strong/weak view: classify each aspect via the tonality thresholds (mentions-gated)
// and return the localized names in the requested bucket.
export function getStrongWeakAspectNames(
    aspectsData: ReviewsByAspectsDetailedItemType['aspectsData'],
    strength: AspectStrengthEnum
): Array<string> {
    return aspectsData
        .filter(
            (aspect) =>
                getAspectStrength(aspect.currentPeriodData.tonalityIdx, aspect.currentPeriodData.reviewsCount) ===
                strength
        )
        .map((aspect) => aspect.aspect);
}

const lineColors = [
    '#E57373',
    '#64B5F6',
    '#81C784',
    '#FFD54F',
    '#BA68C8',
    '#4DD0E1',
    '#AED581',
    '#F06292',
    '#FF8A65',
    '#A1887F',
] as const;

export function getAspectsToneColor(index: number): string {
    const colorIndex = index % lineColors.length;

    const color = lineColors[colorIndex];

    return color ?? lineColors[0];
}

export function getAspectsToneChartOptions(tooltipOptions: DefaultTooltipStaticPropsType): ChartJsLineOptionsType {
    return {
        ...defaultLineChartOptions,
        scales: {
            y: {
                min: -100,
                max: 100,
            },
        },
        plugins: {
            ...defaultLineChartOptions.plugins,
            tooltip: {
                enabled: false,
                yAlign: 'top',
                xAlign: 'center',
                position: 'nearest',
                external: (context: Context) => {
                    customReactChartTooltip({
                        context,
                        componentRenderer: (index, tooltipRendererOptions) => {
                            const label = context.tooltip.dataPoints[0]?.label || '';

                            const datasetLabelsToShowInTooltip = new Set(
                                new Set(
                                    new Set(
                                        context.tooltip.dataPoints?.map((item) => {
                                            return item.dataset.label;
                                        })
                                    )
                                ).values()
                            );

                            const mainPeriodDatasets = tooltipOptions.data.datasets.filter((dataset) => {
                                return datasetLabelsToShowInTooltip.has(dataset.label);
                            });

                            const comparePeriodDatasets =
                                tooltipOptions.compareData?.datasets.filter((dataset) => {
                                    return datasetLabelsToShowInTooltip.has(dataset.label);
                                }) || [];

                            return (
                                <ChartTooltip position={tooltipRendererOptions.position}>
                                    <ChartTooltipTitle title={label} />
                                    {mainPeriodDatasets.map((dataset) => {
                                        return (
                                            <ChartTooltipRow
                                                color={String(dataset.color)}
                                                key={dataset.label}
                                                label={dataset.label}
                                                value={dataset.values?.[index] || 0}
                                            />
                                        );
                                    })}

                                    {comparePeriodDatasets.length > 0 && (
                                        <>
                                            <ChartTooltipDivider />
                                            {comparePeriodDatasets.map((dataset) => {
                                                return (
                                                    <ChartTooltipRow
                                                        color={getCompareColor(String(dataset.color))}
                                                        key={dataset.label}
                                                        label={dataset.label}
                                                        value={dataset.values?.[index] || 0}
                                                    />
                                                );
                                            })}
                                        </>
                                    )}
                                </ChartTooltip>
                            );
                        },
                    });
                },
            },
            legend: {
                display: false,
            },
        },
    };
}

export function getReviewCountByAspectsChartOptions(options: {
    aspects: Array<string>;
    mainPeriodDataset: RadarDataSetType;
    tooltipLabelFormatter: (label: string, compareLabel?: boolean) => string;
    comparePeriodDataset: RadarDataSetType;
    compareMode: boolean;
}): ChartJsRadarOptionsType {
    const {aspects, mainPeriodDataset, tooltipLabelFormatter, comparePeriodDataset, compareMode} = options;

    return {
        ...radarChartOptions,
        scales: {
            // eslint-disable-next-line id-length
            r: {
                min: -100,
                max: 100,
            },
        },
        plugins: {
            ...defaultLineChartOptions.plugins,

            tooltip: {
                enabled: false,
                yAlign: 'top',
                xAlign: 'center',
                position: 'nearest',
                external: (context: Context) =>
                    customReactChartTooltip({
                        context,
                        componentRenderer: (index, tooltipRendererOptions) => {
                            return (
                                <ChartTooltip position={tooltipRendererOptions?.position}>
                                    <ChartTooltipTitle title={tooltipLabelFormatter(mainPeriodDataset.label)} />

                                    <ChartTooltipRow
                                        color={mainPeriodDataset.color.lineColor}
                                        label={aspects[index] || ''}
                                        value={mainPeriodDataset.values[index] || 0}
                                    />

                                    {compareMode && (
                                        <>
                                            <ChartTooltipDivider />

                                            <ChartTooltipTitle
                                                title={tooltipLabelFormatter(comparePeriodDataset.label, true)}
                                            />

                                            <ChartTooltipRow
                                                color={comparePeriodDataset.color.lineColor}
                                                label={aspects[index] || ''}
                                                value={comparePeriodDataset.values[index] || 0}
                                            />
                                        </>
                                    )}
                                </ChartTooltip>
                            );
                        },
                    }),
            },
            legend: {
                display: false,
            },
        },
    };
}
